// This route replays one audited page and pins every finding target onto the snapshot.

import { ChevronRight } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import InspectorFindingsPanel from '@/components/site-health/InspectorFindingsPanel'
import {
  findingRowId,
  pageLabel,
} from '@/components/site-health/SiteHealthFormat'
import {
  ERROR_MESSAGE_CLASS,
  EYEBROW_CLASS,
  MESSAGE_CLASS,
} from '@/components/site-health/SiteHealthStyles'
import SnapshotFrame from '@/components/site-health/SnapshotFrame'
import type {
  InspectorAttention,
  InspectorSelection,
  OverlayPin,
  OverlaySelection,
  SiteHealthPageInspectorParams,
  SnapshotUnavailableProps,
} from '@/components/site-health/siteHealthInspector.types'
import { ApiError } from '@/lib/api'
import { transitions } from '@/lib/motion/presets'
import type {
  SiteHealthInspectorFinding,
  SiteHealthInspectorPage,
} from '@/lib/siteHealth.types'
import {
  useSiteHealthOverview,
  useSiteHealthPageInspector,
  useSiteHealthPageSnapshot,
} from '@/lib/useSiteHealth'
import { useTrackerHref } from '@/provider/useTrackerHref'

const MAIN_CLASS = 'mx-auto w-full max-w-[110rem] px-6 pb-20 pt-10'
const CRUMB_CLASS =
  'text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

function overlayPins(findings: SiteHealthInspectorFinding[]): OverlayPin[] {
  return findings
    .filter((finding) => !finding.pageScoped)
    .flatMap((finding) =>
      finding.targets.map((target) => ({
        findingId: finding.id,
        position: target.position,
        selector: target.selector,
        note: target.note ?? finding.title,
        severity: finding.severity,
      }))
    )
}

function firstTargetPosition(finding: SiteHealthInspectorFinding): number {
  return finding.targets[0]?.position ?? 0
}

// Two branches, not three: a fetched page with no snapshot is oversized or predates snapshots, and the payload cannot tell those apart.
function snapshotUnavailableMessage(page: SiteHealthInspectorPage): string {
  if (page.fetchStatus !== 'succeeded') {
    return 'The audit could not fetch this page, so there is no snapshot to replay. Findings for it stay in the report.'
  }

  return 'This page has no stored snapshot. It was either too large to store, or audited before snapshots were captured.'
}

function SnapshotUnavailable({ page }: SnapshotUnavailableProps) {
  return <div className={MESSAGE_CLASS}>{snapshotUnavailableMessage(page)}</div>
}

export default function SiteHealthPageInspector() {
  const { auditId, pageId } = useParams<SiteHealthPageInspectorParams>()
  const [searchParams] = useSearchParams()
  const href = useTrackerHref()
  const id = auditId ?? ''
  const pid = pageId ?? ''
  const inspectorQuery = useSiteHealthPageInspector(id, pid)
  const overviewQuery = useSiteHealthOverview(id)
  const page = inspectorQuery.data?.page
  const snapshotQuery = useSiteHealthPageSnapshot(
    id,
    pid,
    page?.hasSnapshot === true
  )
  const findings = useMemo(
    () => inspectorQuery.data?.findings ?? [],
    [inspectorQuery.data]
  )
  const pins = useMemo(() => overlayPins(findings), [findings])
  const [selection, setSelection] = useState<InspectorSelection | null>(null)
  const [attention, setAttention] = useState<InspectorAttention | null>(null)
  const preselectedRef = useRef(false)
  const requestedFindingId = searchParams.get('finding')

  useEffect(() => {
    if (preselectedRef.current || findings.length === 0) return

    preselectedRef.current = true

    const requested = findings.find(
      (finding) => finding.id === requestedFindingId
    )

    if (!requested) return

    setSelection({
      findingId: requested.id,
      position: firstTargetPosition(requested),
    })
  }, [findings, requestedFindingId])

  const selectFinding = useCallback((findingId: string, position: number) => {
    setSelection((current) =>
      current?.findingId === findingId && current.position === position
        ? null
        : { findingId, position }
    )
  }, [])

  // Details never toggles. It keeps the row open and flashes it, so a repeat click still reads.
  const openFindingDetails = useCallback(
    (findingId: string, position: number) => {
      setSelection({ findingId, position })
      setAttention((current) => ({
        findingId,
        nonce: (current?.nonce ?? 0) + 1,
      }))

      document
        .getElementById(findingRowId(findingId))
        ?.scrollIntoView({ block: 'nearest' })
    },
    []
  )

  const overlaySelection: OverlaySelection | null = useMemo(() => {
    if (!selection) return null

    return (
      pins.find(
        (candidate) =>
          candidate.findingId === selection.findingId &&
          candidate.position === selection.position
      ) ?? null
    )
  }, [pins, selection])

  if (!auditId || !pageId) {
    return (
      <main className={MAIN_CLASS}>
        <div className={ERROR_MESSAGE_CLASS}>
          The Site Health page reference is missing.
        </div>
      </main>
    )
  }

  if (inspectorQuery.isPending) {
    return (
      <main className={MAIN_CLASS}>
        <div className={MESSAGE_CLASS}>Loading page inspector…</div>
      </main>
    )
  }

  if (inspectorQuery.isError || !inspectorQuery.data) {
    return (
      <main className={MAIN_CLASS}>
        <div className={ERROR_MESSAGE_CLASS}>
          {isNotFound(inspectorQuery.error)
            ? 'Page not found.'
            : 'The page inspector could not be loaded. Refresh to try again.'}
        </div>
      </main>
    )
  }

  const inspected = inspectorQuery.data.page

  return (
    <main className={MAIN_CLASS}>
      <motion.header
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitions.panel}
      >
        <nav
          aria-label="Breadcrumb"
          className={`flex items-center gap-1.5 ${EYEBROW_CLASS}`}
        >
          <Link to={href('/site-health')} className={CRUMB_CLASS}>
            Site Health
          </Link>
          <ChevronRight className="size-3.5" aria-hidden />
          <Link
            to={href(`/site-health/${auditId}?view=pages`)}
            className={CRUMB_CLASS}
          >
            {overviewQuery.data ? pageLabel(overviewQuery.data.url) : 'Audit'}
          </Link>
        </nav>
        <h1 className="mt-2 break-all text-2xl font-semibold tracking-tight">
          {pageLabel(inspected.url)}
        </h1>
      </motion.header>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div>
          {!inspected.hasSnapshot && <SnapshotUnavailable page={inspected} />}
          {inspected.hasSnapshot && snapshotQuery.isPending && (
            <div className={MESSAGE_CLASS}>Loading page snapshot…</div>
          )}
          {inspected.hasSnapshot && snapshotQuery.isError && (
            <div className={ERROR_MESSAGE_CLASS}>
              The stored snapshot could not be loaded. Refresh to try again.
            </div>
          )}
          {inspected.hasSnapshot && snapshotQuery.data && (
            <SnapshotFrame
              html={snapshotQuery.data.html}
              pageUrl={inspected.url}
              pins={pins}
              selection={overlaySelection}
              onSelect={selectFinding}
              onOpenDetails={openFindingDetails}
            />
          )}
        </div>

        <InspectorFindingsPanel
          findings={findings}
          selectedFindingId={selection?.findingId ?? null}
          attention={attention}
          onSelect={selectFinding}
        />
      </div>
    </main>
  )
}
